import React, { useState, useEffect } from 'react';
import { ViewDisplaySettings } from '@/app/types/cad';
import { X } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface BlockDisplayOverrideDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  blockName: string;
  currentOverrides?: Partial<ViewDisplaySettings>;
  currentColorOverride?: string;
  globalSettings: ViewDisplaySettings;
  onSave: (overrides: Partial<ViewDisplaySettings> | undefined, colorOverride?: string) => void;
}

export function BlockDisplayOverrideDialog({
  open,
  onOpenChange,
  blockName,
  currentOverrides,
  currentColorOverride,
  globalSettings,
  onSave,
}: BlockDisplayOverrideDialogProps) {
  const { t } = useLanguage();
  const [useOverrides, setUseOverrides] = useState(!!currentOverrides);
  const [lineWidthMultiplier, setLineWidthMultiplier] = useState(
    currentOverrides?.lineWidthMultiplier ?? globalSettings.lineWidthMultiplier
  );
  const [connectionSizeMultiplier, setConnectionSizeMultiplier] = useState(
    currentOverrides?.connectionSizeMultiplier ?? globalSettings.connectionSizeMultiplier
  );
  const [fontSizeMultiplier, setFontSizeMultiplier] = useState(
    currentOverrides?.fontSizeMultiplier ?? globalSettings.fontSizeMultiplier
  );
  const [colorOverride, setColorOverride] = useState(currentColorOverride);

  useEffect(() => {
    if (open) {
      setUseOverrides(!!currentOverrides);
      setLineWidthMultiplier(currentOverrides?.lineWidthMultiplier ?? globalSettings.lineWidthMultiplier);
      setConnectionSizeMultiplier(currentOverrides?.connectionSizeMultiplier ?? globalSettings.connectionSizeMultiplier);
      setFontSizeMultiplier(currentOverrides?.fontSizeMultiplier ?? globalSettings.fontSizeMultiplier);
      setColorOverride(currentColorOverride);
    }
  }, [open, currentOverrides, currentColorOverride, globalSettings]);

  const handleSave = () => {
    if (useOverrides) {
      onSave({
        lineWidthMultiplier,
        connectionSizeMultiplier,
        fontSizeMultiplier,
      }, colorOverride);
    } else {
      onSave(undefined); // Remove overrides
    }
    onOpenChange(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={() => onOpenChange(false)}
      />

      {/* Dialog */}
      <div
        className="relative w-full max-w-lg mx-4 rounded-lg shadow-lg"
        style={{
          backgroundColor: 'var(--md-surface-container-high)',
          color: 'var(--md-on-surface)',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 border-b"
          style={{ borderColor: 'var(--md-outline)' }}
        >
          <h2 className="text-lg font-semibold">{t('dialogs.blockDisplay.title')}: {blockName}</h2>
          <button
            onClick={() => onOpenChange(false)}
            className="p-1 rounded hover:bg-black/10"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Use Overrides Toggle */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="use-overrides"
              checked={useOverrides}
              onChange={(e) => setUseOverrides(e.target.checked)}
              className="w-4 h-4"
            />
            <label htmlFor="use-overrides" className="text-sm">
              {t('dialogs.blockDisplay.useOverrides')}
            </label>
          </div>

          {useOverrides && (
            <>
              <div className="text-xs opacity-70 mb-4">
                {t('dialogs.blockDisplay.overridesDesc')}
              </div>

              {/* Line Width Multiplier */}
              <div>
                <label className="block text-sm mb-2">
                  {t('dialogs.blockDisplay.lineWidth')}: {lineWidthMultiplier.toFixed(2)}x
                  <span className="text-xs opacity-70 ml-2">
                    ({t('dialogs.blockDisplay.global')}: {globalSettings.lineWidthMultiplier.toFixed(2)}x)
                  </span>
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    type="range"
                    min="0.1"
                    max="5"
                    step="0.1"
                    value={lineWidthMultiplier}
                    onChange={(e) => setLineWidthMultiplier(parseFloat(e.target.value))}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    min="0.1"
                    max="5"
                    step="0.1"
                    value={lineWidthMultiplier}
                    onChange={(e) => setLineWidthMultiplier(parseFloat(e.target.value))}
                    className="w-20"
                  />
                </div>
              </div>

              {/* Connection Size Multiplier */}
              <div>
                <label className="block text-sm mb-2">
                  {t('dialogs.blockDisplay.connectionSize')}: {connectionSizeMultiplier.toFixed(2)}x
                  <span className="text-xs opacity-70 ml-2">
                    ({t('dialogs.blockDisplay.global')}: {globalSettings.connectionSizeMultiplier.toFixed(2)}x)
                  </span>
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    type="range"
                    min="0.1"
                    max="5"
                    step="0.1"
                    value={connectionSizeMultiplier}
                    onChange={(e) => setConnectionSizeMultiplier(parseFloat(e.target.value))}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    min="0.1"
                    max="5"
                    step="0.1"
                    value={connectionSizeMultiplier}
                    onChange={(e) => setConnectionSizeMultiplier(parseFloat(e.target.value))}
                    className="w-20"
                  />
                </div>
              </div>

              {/* Font Size Multiplier */}
              <div>
                <label className="block text-sm mb-2">
                  {t('dialogs.blockDisplay.fontSize')}: {fontSizeMultiplier.toFixed(2)}x
                  <span className="text-xs opacity-70 ml-2">
                    ({t('dialogs.blockDisplay.global')}: {globalSettings.fontSizeMultiplier.toFixed(2)}x)
                  </span>
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    type="range"
                    min="0.1"
                    max="5"
                    step="0.1"
                    value={fontSizeMultiplier}
                    onChange={(e) => setFontSizeMultiplier(parseFloat(e.target.value))}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    min="0.1"
                    max="5"
                    step="0.1"
                    value={fontSizeMultiplier}
                    onChange={(e) => setFontSizeMultiplier(parseFloat(e.target.value))}
                    className="w-20"
                  />
                </div>
              </div>

              {/* Color Override */}
              <div>
                <label className="block text-sm mb-2">
                  {t('dialogs.blockDisplay.colorOverride')}:
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    type="color"
                    value={colorOverride || '#000000'}
                    onChange={(e) => setColorOverride(e.target.value)}
                    className="w-20"
                  />
                  <span className="text-xs opacity-70">
                    {colorOverride || t('attributes.original')}
                  </span>
                  {colorOverride && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setColorOverride(undefined)}
                    >
                      {t('common.reset')}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}

          {!useOverrides && (
            <div className="text-sm opacity-70 py-4">
              {t('dialogs.blockDisplay.noOverrides')}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex justify-end gap-2 p-4 border-t"
          style={{ borderColor: 'var(--md-outline)' }}
        >
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave}>
            {t('common.apply')}
          </Button>
        </div>
      </div>
    </div>
  );
}